import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Result, Button } from 'antd';
import i18n from 'i18next';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div style={{ padding: '50px', textAlign: 'center' }}>
          <Result
            status="error"
            title={i18n.t('common:errorBoundary.title')}
            subTitle={this.state.error?.message || i18n.t('common:errorBoundary.unexpectedError')}
            extra={[
              <Button type="primary" key="reload" onClick={() => window.location.reload()}>
                {i18n.t('common:errorBoundary.reload')}
              </Button>,
              <Button key="reset" onClick={this.handleReset}>
                {i18n.t('common:errorBoundary.tryAgain')}
              </Button>,
            ]}
          />
        </div>
      );
    }

    return this.props.children;
  }
}
